import { useContext, useEffect, useState } from "react";
import { Button, Grid, MenuItem, Typography, Tooltip, IconButton, CircularProgress } from "@material-ui/core";
import { SaveOutlined, Cancel as CancelIcon } from "@material-ui/icons";
import { ErrorMessage, Field, FieldArray, FieldProps, useFormikContext } from "formik";
import { TextField } from 'formik-material-ui';
import { SaveResponse } from "helpers/interfaces";
import { applicationContext, appUserContext } from "helpers/services";
import axios from 'axios';
import ReactQuill from "react-quill";
import { INewTicket } from '../NewTicketContext';
import { SCAutocomplete, Dropzone } from "components";
import { ReferenceData, TypeInfo, UserType } from "helpers/enums";
import { KeyValuePair } from "helpers";
import DeliveryInfo from "./DeliveryInfo";
import ProductInfo from "./ProductInfo";

let NewTicketForm = ({ isSaving }: { isSaving: boolean }) => {
    let { referenceData, ticketTypes, ticketPriorities, searchReporters } = useContext(applicationContext);
    let { currentUser } = useContext(appUserContext);

    let form = useFormikContext<INewTicket>();

    let [isUploading, setUploading] = useState(false);


    let dropCallback = (files: any[]) => {
        files.forEach(async (element: any) => {
            try {
                setUploading(true);
                let formData = new FormData();
                formData.append("file", element);

                let res = await axios.post<SaveResponse>('/api/TicketMessage/UploadAttachment', formData);

                form.values.attachments.push({
                    fileId: res.data.id,
                    fileName: element.name
                });

                form.setFieldValue('attachments', [...form.values.attachments]);

            } catch (err) {
                form.setFieldError("attachments", "Error during file upload");
            } finally {
                setUploading(false);
            }
        })
    }


    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Field
                        component={TextField}
                        select
                        name="ticketTypeId"
                        label="Typology"
                        variant="outlined"
                        fullWidth
                        onChange={(e: any) => {
                            form.setFieldValue("ticketTypeId", e.target.value);
                            form.setFieldValue("typeInfo", ticketTypes.getValueByKey(e.target.value, 'id', 'typeInfo'));
                            form.setFieldValue("issueServiceType", '');
                        }}
                    >
                        {ticketTypes.map(x => <MenuItem key={x.id} value={x.id}>{x.name}</MenuItem>)}
                    </Field>
                </Grid>
                <Grid item xs={12}>
                    <Field
                        component={TextField}
                        select
                        name="ticketPriorityId"
                        label="Priority"
                        variant="outlined"
                        fullWidth
                    >
                        {ticketPriorities.map(x => <MenuItem key={x.id} value={x.id}>{x.description}</MenuItem>)}
                    </Field>
                </Grid>
                <Grid item xs={12}>
                    <Field
                        component={TextField}
                        select
                        label="Supplier"
                        name="supplier"
                        variant="outlined"
                        size="small"
                        fullWidth
                    >
                        {referenceData
                            .getByKey(ReferenceData.Supplier)?.values
                            .map((x: KeyValuePair<string, string>) => <MenuItem key={x.key} value={x.key}>{x.value}</MenuItem>)
                        }
                    </Field>
                </Grid>
                <Grid item xs={12}>
                    <Field
                        component={TextField}
                        name="title"
                        label="Title"
                        variant="outlined"
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" color={form.touched.message && form.errors.message ? "error" : "textSecondary"}>Description</Typography>
                    <Field name="message">
                        {({ field }: FieldProps) =>
                            <ReactQuill value={field.value} onChange={field.onChange(field.name)} />
                        }
                    </Field>
                    <ErrorMessage name="message">
                        {msg => <Typography variant="caption" color="error">{msg}</Typography>}
                    </ErrorMessage>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">Attachments</Typography>
                    <Dropzone isMultiple={true} onDropCallback={dropCallback} />
                    {isUploading && <CircularProgress size={20} />}
                    {form.values.attachments.length > 0 &&
                        <FieldArray name="attachments">
                            {arrayHelper =>
                                form.values.attachments.map((file, index) =>
                                    <Typography key={index}>
                                        File uploaded:&nbsp;{file.fileName}
                                        <Tooltip arrow title="Remove">
                                            <IconButton
                                                size="small"
                                                onClick={() => arrayHelper.remove(index)}
                                            >
                                                <CancelIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Typography>
                                )
                            }
                        </FieldArray>
                    }
                    {form.values.typeInfo === TypeInfo.Product &&
                        <Typography variant="caption" color="secondary">Remember to add the picture of packaging label, otherwise the ticket can't be processed</Typography>
                    }
                </Grid>
            </Grid>

            {currentUser?.typology === UserType.VitecUser &&
                <Grid container>
                    <Grid item xs>
                        <SCAutocomplete
                            inputLabel="Raise this request on behalf of"
                            variant="outlined"
                            value={{
                                value: form.values.reporter,
                                label: form.values.reporterName
                            }}
                            doSearch={async (value: string, etag: number) => await searchReporters(value, etag)}
                            onChange={(value: any) => {
                                form.setFieldValue('reporter', value?.value || '');
                                form.setFieldValue('reporterName', value?.label || '');
                            }}
                            error={Boolean(form.touched.reporter && form.errors.reporter)}
                            helperText={form.errors.reporter || 'Type in the name of a customer or colleague'}
                        />
                    </Grid>
                </Grid>
            }

            {form.values.typeInfo === TypeInfo.Delivery && <DeliveryInfo />}
            {form.values.typeInfo === TypeInfo.Product && <ProductInfo />}

            <Grid container>
                <Grid item xs />
                <Grid item>
                    <Button
                        type="submit"
                        disabled={isSaving}
                        color="secondary"
                        variant="contained"
                    >
                        {isSaving ? <CircularProgress size={16} color="secondary" /> : <SaveOutlined />}&nbsp;Save
                    </Button>
                </Grid>
            </Grid>
        </>
    )
}

export default NewTicketForm;